import React, { useState, useRef, useEffect } from "react";
import { Edit2, Image, X, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { uploadCommunityPostImage } from "@/services/imageUploadService";
import { useAuth } from "@/hooks/useAuth";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { CommunityPost } from "@/types/community";

interface EditPostModalProps {
  post: CommunityPost | null;
  isOpen: boolean;
  onClose: () => void;
  onPostUpdated: () => void;
  onUpdatePost: (postId: string, data: { title: string; content: string; image_url?: string }) => Promise<void>;
  isUpdating: boolean;
}

export const EditPostModal: React.FC<EditPostModalProps> = ({
  post,
  isOpen,
  onClose,
  onPostUpdated,
  onUpdatePost,
  isUpdating
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: ''
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Carregar dados do post quando o modal abrir
  useEffect(() => {
    if (isOpen && post) {
      setFormData({
        title: post.title || '',
        content: post.content || '',
        image_url: post.media_url || ''
      });
    }
  }, [isOpen, post]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!post) return;
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Título e conteúdo são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('✏️ [EditPostModal] Atualizando post:', post.id, formData);
      
      await onUpdatePost(post.id, {
        title: formData.title.trim(),
        content: formData.content.trim(),
        image_url: formData.image_url.trim() || undefined
      });
      
      toast({
        title: "Post atualizado!",
        description: "As alterações foram salvas com sucesso.",
      });
      
      onClose();
      onPostUpdated();
      
    } catch (error: any) {
      console.error('❌ [EditPostModal] Erro ao atualizar post:', error);
      toast({
        title: "Erro ao atualizar",
        description: error.message || "Não foi possível atualizar o post. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    // Resetar formulário e fechar
    if (post) {
      setFormData({
        title: post.title || '',
        content: post.content || '',
        image_url: post.media_url || ''
      });
    }
    onClose();
  };

  const handleImageUpload = async (file: File) => {
    if (!user?.id) {
      toast({
        title: "Erro de autenticação",
        description: "Usuário não encontrado.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingImage(true);
    
    try {
      const result = await uploadCommunityPostImage(file, user.id);
      
      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, image_url: result.url! }));
        toast({
          title: "Imagem enviada!",
          description: "A imagem foi hospedada em nossos servidores com sucesso.",
        });
      } else {
        toast({
          title: "Erro no upload",
          description: result.error || "Erro desconhecido ao fazer upload da imagem.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro no upload",
        description: "Erro inesperado ao fazer upload da imagem.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Limpar o input para permitir upload do mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!post) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()} modal>
      <DialogContent 
        className="bg-[#1A2333] border-[#222A35] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-[#00B6F0]" />
            Editar Post
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Faça as alterações necessárias no seu post
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="edit-title" className="text-sm font-medium text-gray-300">
              Título do Post *
            </Label>
            <Input
              id="edit-title"
              placeholder="Digite um título chamativo..."
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="bg-[#242C37] border-[#333A45] text-white placeholder-gray-400 focus:border-[#00B6F0]"
              maxLength={200}
              required
            />
            <div className="text-xs text-gray-400 text-right">
              {formData.title.length}/200
            </div>
          </div>
          
          {/* Conteúdo */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-300">
              Conteúdo *
            </Label>
            <RichTextEditor
              value={formData.content}
              onChange={(content) => setFormData(prev => ({ ...prev, content }))}
              placeholder="O que você gostaria de compartilhar com a comunidade?"
              maxLength={5000}
              showPreview={true}
              disabled={isUpdating}
            />
          </div>
          
          {/* Imagem */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Image size={16} />
              Imagem (Opcional)
            </Label>
            
            {/* Botão de Upload */}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#333A45] bg-[#242C37] hover:bg-[#2A3441] text-gray-300"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage || isUpdating}
              >
                {isUploadingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300 mr-2"></div>
                    Enviando imagem...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Hospedar Nova Imagem
                  </>
                )}
              </Button>
              <div className="text-xs text-gray-400 text-center">
                ou cole uma URL de imagem abaixo
              </div>
            </div>
            
            {/* Campo de URL */}
            <Input
              placeholder="Cole a URL de uma imagem..."
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              className="bg-[#242C37] border-[#333A45] text-white placeholder-gray-400 focus:border-[#00B6F0]"
              type="url"
            />
            
            {/* Preview da imagem */}
            {formData.image_url && (
              <div className="relative">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full max-h-48 object-cover rounded-lg"
                  onError={() => {
                    toast({
                      title: "Imagem inválida",
                      description: "A URL da imagem não é válida.",
                      variant: "destructive",
                    });
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                >
                  <X size={14} />
                </Button>
              </div>
            )}
            
            <div className="text-xs text-gray-400">
              Aceita imagens até 5MB (.jpg, .png, .gif, .webp). Imagens hospedadas em nossos servidores ficam sempre disponíveis.
            </div>
          </div>
          
          {/* Ações */}
          <div className="flex gap-3 pt-4 border-t border-[#333A45]">
            <Button
              type="submit"
              disabled={isUpdating || !formData.title.trim() || !formData.content.trim()}
              className="flex-1 bg-[#00B6F0] hover:bg-[#0095CC] text-black font-medium"
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Edit2 size={16} className="mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isUpdating}
              className="border-[#333A45] text-gray-300 hover:bg-[#242C37] hover:text-white"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 